// src/components/TipTapEditor.jsx
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { useEffect, useRef, useState } from "react";

export default function TipTapEditor({ value, onChange, onUploadImage }) {
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState("edit"); // "edit" | "preview"

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Image,
      Link.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
    ],
    content: value || "",
    onUpdate({ editor }) {
      const html = editor.getHTML();
      onChange?.(html);
    },
  });

  // Keep editor in sync if parent changes value
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value != null && value !== current) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const triggerImageUpload = () => {
    if (!fileInputRef.current) return;
    fileInputRef.current.value = "";
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !onUploadImage) return;

    try {
      const result = await onUploadImage(file);

      // Support either a plain string or an object like { url } / { publicUrl }
      const url =
        typeof result === "string"
          ? result
          : result?.url || result?.publicUrl || null;

      if (!url) {
        console.error(
          "Image upload did not return a usable URL. Got:",
          result
        );
        return;
      }

      editor
        .chain()
        .focus()
        .setImage({ src: url, alt: file.name })
        .run();
    } catch (err) {
      console.error("Image upload failed in TipTapEditor:", err);
    }
  };

  const toggleLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("Enter URL", previousUrl || "");

    if (url === null) return; // cancelled

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url })
      .run();
  };

  const stats = (() => {
    const text = editor.getText() || "";
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    const chars = text.length;
    const minutes = Math.max(1, Math.round(words / 200)); // ~200 wpm
    return { words, chars, minutes };
  })();

  return (
    <div style={wrapperStyle}>
      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Header: Edit / Preview tabs + stats */}
      <div style={headerRowStyle}>
        <div style={tabGroupStyle}>
          <button
            type="button"
            onClick={() => setActiveTab("edit")}
            style={tabButtonStyle(activeTab === "edit")}
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("preview")}
            style={tabButtonStyle(activeTab === "preview")}
          >
            Preview
          </button>
        </div>
        <div style={statsStyle}>
          <span>{stats.words} words</span>
          <span>· {stats.minutes} min read</span>
        </div>
      </div>

      {/* Toolbar (only in Edit mode) */}
      {activeTab === "edit" && (
        <div style={toolbarStyle}>
          {/* Block styles */}
          <button
            type="button"
            onClick={() => editor.chain().focus().setParagraph().run()}
            style={buttonStyle(editor.isActive("paragraph"))}
          >
            ¶
          </button>
          <button
            type="button"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
            style={buttonStyle(editor.isActive("heading", { level: 1 }))}
          >
            H1
          </button>
          <button
            type="button"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            style={buttonStyle(editor.isActive("heading", { level: 2 }))}
          >
            H2
          </button>
          <button
            type="button"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
            style={buttonStyle(editor.isActive("heading", { level: 3 }))}
          >
            H3
          </button>

          <div style={toolbarDividerStyle} />

          {/* Inline styles */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            style={buttonStyle(editor.isActive("bold"))}
          >
            B
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            style={buttonStyle(editor.isActive("italic"))}
          >
            I
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            style={buttonStyle(editor.isActive("strike"))}
          >
            S
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleCode().run()}
            style={buttonStyle(editor.isActive("code"))}
          >
            {"</>"}
          </button>

          <div style={toolbarDividerStyle} />

          {/* Lists / block quotes */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            style={buttonStyle(editor.isActive("bulletList"))}
          >
            • List
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            style={buttonStyle(editor.isActive("orderedList"))}
          >
            1. List
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            style={buttonStyle(editor.isActive("blockquote"))}
          >
            ❝ Quote
          </button>

          <div style={toolbarDividerStyle} />

          {/* Alignment */}
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
            style={buttonStyle(editor.isActive({ textAlign: "left" }))}
          >
            ⬅
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
            style={buttonStyle(editor.isActive({ textAlign: "center" }))}
          >
            ⬍
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
            style={buttonStyle(editor.isActive({ textAlign: "right" }))}
          >
            ➡
          </button>

          <div style={toolbarDividerStyle} />

          {/* Links / images */}
          <button
            type="button"
            onClick={toggleLink}
            style={buttonStyle(editor.isActive("link"))}
          >
            🔗
          </button>
          <button
            type="button"
            onClick={triggerImageUpload}
            style={buttonStyle(false)}
          >
            🖼 Image
          </button>

          <div style={toolbarDividerStyle} />

          {/* Other actions */}
          <button
            type="button"
            onClick={() =>
              editor.chain().focus().setHorizontalRule().run()
            }
            style={buttonStyle(false)}
          >
            ― HR
          </button>
        </div>
      )}

      {/* Editor / Preview body */}
      <div style={bodyShellStyle}>
        {activeTab === "edit" ? (
          <div style={editorShellStyle}>
            <EditorContent editor={editor} />
          </div>
        ) : (
          <div style={previewShellStyle}>
            {editor.getText().trim() ? (
              <div
                style={previewContentStyle}
                dangerouslySetInnerHTML={{ __html: editor.getHTML() }}
              />
            ) : (
              <div style={previewEmptyStyle}>
                Your post preview will appear here.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* Styles */

const wrapperStyle = {
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  overflow: "hidden",
  background: "white",
  display: "flex",
  flexDirection: "column",
};

const headerRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "6px 10px",
  borderBottom: "1px solid #e5e7eb",
  background: "#f9fafb",
};

const tabGroupStyle = {
  display: "inline-flex",
  borderRadius: 999,
  border: "1px solid #d1d5db",
  overflow: "hidden",
};

const tabButtonStyle = (active) => ({
  padding: "4px 10px",
  border: "none",
  background: active ? "#111827" : "white",
  color: active ? "white" : "#111827",
  fontSize: "0.8rem",
  cursor: "pointer",
  outline: "none",
});

const statsStyle = {
  fontSize: "0.75rem",
  color: "#6b7280",
  display: "flex",
  gap: 6,
};

const toolbarStyle = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 6,
  padding: "6px 8px",
  background: "#f3f4f6",
  borderBottom: "1px solid #e5e7eb",
};

const toolbarDividerStyle = {
  width: 1,
  alignSelf: "stretch",
  background: "#e5e7eb",
  margin: "0 4px",
};

const bodyShellStyle = {
  display: "flex",
  minHeight: 200,
};

const editorShellStyle = {
  flex: 1,
  padding: 10,
};

const previewShellStyle = {
  flex: 1,
  padding: 10,
  background: "#f9fafb",
};

const previewContentStyle = {
  maxWidth: 720,
  margin: "0 auto",
  lineHeight: 1.7,
  fontSize: "0.95rem",
};

const previewEmptyStyle = {
  textAlign: "center",
  color: "#9ca3af",
  fontSize: "0.9rem",
  padding: "40px 0",
};

const buttonStyle = (active) => ({
  padding: "3px 7px",
  borderRadius: 999,
  border: "1px solid #d1d5db",
  background: active ? "#111827" : "white",
  color: active ? "white" : "#111827",
  fontSize: "0.75rem",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: 4,
  whiteSpace: "nowrap",
});
